import {
  ActionIcon,
  Alert,
  Anchor,
  Autocomplete,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconCalendar,
  IconCar,
  IconExternalLink,
  IconEye,
  IconPlus,
  IconReceipt,
  IconSearch,
  IconTool,
  IconTrash,
  IconUserCheck,
  IconUsers,
} from '@tabler/icons-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { denar } from '../oblika/oblika';
import { imeOsebe, naslov } from '../osebe/vrste';
import { Kartica, Podatki } from '../postavitev/Podatki';
import { barve } from '../tema';
import { useProdaja } from '../seja/paket';
import { IzbiraOsebe } from '../vozila/IzbiraOsebe';
import { KORAKI, stevilka, type Korak } from './izdaja';

type Oseba = Shema['OsebaPodatki'];
type Vozilo = Shema['VoziloPodatki'];
type PostavkaVozila = Shema['PostavkaVozila'];
type PostavkaStoritve = Shema['PostavkaStoritve'];
type Klavzula = Shema['KlavzulaPodatki'];

const IKONE: Record<Korak, ReactNode> = {
  kupec: <IconUsers size={18} />,
  artikli: <IconCar size={18} />,
  storitve: <IconTool size={18} />,
  roki: <IconCalendar size={18} />,
  predogled: <IconReceipt size={18} />,
};

/**
 * Koraki izdaje kot TemaKoraki v Swingu: na namizju levo navpično, na telefonu zgoraj. Korak z napako (po poskusu
 * izdaje) ima rdečo piko. Brez modula Prodaja ni koraka Artikli (vozila z zaloge).
 */
export function KorakiIzdaje({
  korak,
  izberi,
  napake,
  vsebina,
}: {
  korak: Korak;
  izberi: (k: Korak) => void;
  napake: Set<Korak>;
  vsebina: Record<Korak, ReactNode>;
}) {
  const siroko = useMediaQuery('(min-width: 62em)');
  const koraki = useKorakiPaketa();
  return (
    <Tabs
      value={korak}
      onChange={(v) => v && izberi(v as Korak)}
      orientation={siroko ? 'vertical' : 'horizontal'}
      variant="pills"
      keepMounted={false}
    >
      <Tabs.List
        mb={siroko ? 0 : 'md'}
        mr={siroko ? 'lg' : 0}
        style={siroko ? { minWidth: 180 } : { flexWrap: 'nowrap', overflowX: 'auto' }}
      >
        {koraki.map((k) => (
          <Tabs.Tab
            key={k.kljuc}
            value={k.kljuc}
            leftSection={siroko ? IKONE[k.kljuc] : undefined}
            rightSection={
              napake.has(k.kljuc) ? (
                <Box w={8} h={8} bg={barve.napaka} style={{ borderRadius: 4 }} aria-label="napaka" />
              ) : undefined
            }
          >
            {k.naziv}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {koraki.map((k) => (
        <Tabs.Panel key={k.kljuc} value={k.kljuc} style={{ minWidth: 0 }}>
          {vsebina[k.kljuc]}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}

/** Koraki izdaje za paket instance: brez modula Prodaja brez koraka Artikli. */
function useKorakiPaketa() {
  const prodaja = useProdaja();
  return KORAKI.filter((k) => prodaja || k.kljuc !== 'artikli');
}

/** Gumb na koncu koraka, ki odpre naslednjega (na telefonu so zavihki korakov zgoraj in jih je lahko spregledati). */
export function Naprej({ na: zeleni, izberi }: { na: Korak; izberi: (k: Korak) => void }) {
  // brez modula Prodaja koraka Artikli ni: naprej na Storitve
  const prodaja = useProdaja();
  const na = !prodaja && zeleni === 'artikli' ? 'storitve' : zeleni;
  return (
    <Group justify="flex-end" mt="md">
      <Button variant="default" onClick={() => izberi(na)}>
        Naprej: {KORAKI.find((k) => k.kljuc === na)?.naziv}
      </Button>
    </Group>
  );
}

function PodatkiOsebe({ o, vrsta }: { o: Oseba; vrsta: 'kupci' | 'uporabniki-vozil' }) {
  return (
    <Stack gap="xs">
      <Podatki
        vrstice={[
          ['Ime', imeOsebe(o)],
          ['Naslov', naslov(o)],
          ['Davčna št.', o.davcna],
          ['ID za DDV', o.idZaDdv],
          ['EMŠO', o.emso],
          ['OIB', o.oib],
          ['Matična št.', o.maticnaSt],
          ['Št. dokumenta', o.stDokumenta],
        ]}
      />
      <Anchor component={Link} to={`/osebe/${vrsta}/${o.id}`} target="_blank" size="sm">
        <Group gap={4} component="span">
          Odpri osebo <IconExternalLink size={14} />
        </Group>
      </Anchor>
    </Stack>
  );
}

/**
 * Korak Kupec (kot KupecPanel): kupec in uporabnik vozila z iskanjem na strežniku. Brez uporabnika vozila ga lahko
 * strežnik naredi iz kupca (obstoječi z enakimi podatki ali kopija) – prej je to naredil Swing pred izdajo.
 */
export function OsebeIzdaje({
  kupec,
  uporabnik,
  izberiKupca,
  izberiUporabnika,
  napake,
  zVozili,
}: {
  kupec?: Oseba | null;
  uporabnik?: Oseba | null;
  izberiKupca: (o: Oseba | null) => void;
  izberiUporabnika: (o: Oseba | null) => void;
  napake: Record<string, string>;
  zVozili: boolean;
}) {
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const izKupca = async () => {
    if (!kupec?.id) {
      return;
    }
    setNapaka(null);
    setDela(true);
    try {
      izberiUporabnika(
        await podatki(
          api.POST('/api/osebe/uporabniki-vozil/iz-kupca/{kupecId}', { params: { path: { kupecId: kupec.id } } }),
        ),
      );
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      <Kartica naslov="Kupec">
        <Stack gap="sm">
          <IzbiraOsebe vrsta="kupci" oznaka="Kupec" izbran={kupec} izberi={izberiKupca} napaka={napake.kupecId} />
          {kupec?.id ? <PodatkiOsebe o={kupec} vrsta="kupci" /> : null}
        </Stack>
      </Kartica>
      <Kartica naslov="Uporabnik vozila">
        <Stack gap="sm">
          <IzbiraOsebe
            vrsta="uporabniki-vozil"
            oznaka="Uporabnik vozila"
            obvezno={zVozili}
            izbran={uporabnik}
            izberi={izberiUporabnika}
            napaka={napake.uporabnikVozilaId}
          />
          {uporabnik?.id ? (
            <PodatkiOsebe o={uporabnik} vrsta="uporabniki-vozil" />
          ) : (
            kupec?.id && (
              <Group>
                <Button
                  variant="light"
                  leftSection={<IconUserCheck size={18} />}
                  onClick={() => void izKupca()}
                  loading={dela}
                >
                  Kupec je tudi uporabnik vozila
                </Button>
              </Group>
            )
          )}
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
        </Stack>
      </Kartica>
    </SimpleGrid>
  );
}

const opisVozila = (v: Vozilo) =>
  [v.znamka, v.tip, v.vin, v.regSt, v.financirano ? 'financirano (leasing)' : null].filter(Boolean).join(' · ');
const ddv = (s?: number | null) => (s === undefined || s === null ? '–' : `${String(s).replace('.', ',')} %`);

/**
 * Korak Artikli (kot IzbiraVozilaPanel): vozila na zalogi z iskanjem; cena postavke je prodajna cena vozila –
 * sprememba cene ali stopnje DDV se shrani na vozilo (»shrani se tudi za naprej«, kot v Swingu). {@code financirana}:
 * tudi vozila, financirana pri leasingu (predračun).
 */
export function VozilaIzdaje({
  vozila,
  dodaj,
  odstrani,
  napaka,
  financirana = false,
}: {
  vozila: PostavkaVozila[];
  dodaj: (v: Vozilo) => void;
  odstrani: (index: number) => void;
  napaka?: string;
  financirana?: boolean;
}) {
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje, 300);
  const [ureja, setUreja] = useState<{ v: PostavkaVozila; kaj: 'cena' | 'ddv' } | null>(null);
  const zadetki = useQuery({
    queryKey: ['vozila-iskanje', zakasnjeno, financirana],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/vozila/iskanje', {
          signal,
          params: { query: { iskanje: zakasnjeno || undefined, financirana: financirana || undefined } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const dodana = new Set(vozila.map((v) => v.voziloId));
  const moznosti = new Map<string, Vozilo>();
  (zadetki.data ?? [])
    .filter((v) => !dodana.has(v.id))
    .slice(0, 30)
    .forEach((v) => moznosti.set(String(v.id), v));

  return (
    <Stack gap="md">
      <Kartica naslov="Izbira vozila">
        <Select
          label="Vozilo na zalogi"
          placeholder="Znamka, tip, VIN ali vezan dokument"
          searchable
          leftSection={<IconSearch size={16} />}
          data={[...moznosti.entries()].map(([value, v]) => ({ value, label: opisVozila(v) }))}
          value={null}
          onChange={(v) => {
            const izbrano = v ? moznosti.get(v) : undefined;
            if (izbrano) {
              dodaj(izbrano);
              setIskanje('');
            }
          }}
          searchValue={iskanje}
          onSearchChange={setIskanje}
          filter={({ options }) => options}
          nothingFoundMessage={zadetki.isFetching ? 'Iščem …' : 'Ni vozil na zalogi'}
          error={napaka}
        />
      </Kartica>
      <Kartica naslov="Dodana vozila">
        {vozila.length === 0 ? (
          <Text size="sm" c="dimmed">
            Ni dodanih vozil.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={620}>
            <Table className="stevilke" verticalSpacing={6}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vozilo</Table.Th>
                  <Table.Th>VIN</Table.Th>
                  <Table.Th ta="right">DDV</Table.Th>
                  <Table.Th ta="right">Prodajna cena</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {vozila.map((v, i) => (
                  <Table.Tr key={`${v.voziloId}-${i}`}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {[v.znamka, v.tip].filter(Boolean).join(' ')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {[v.regSt, v.posredniskaId ? 'posredniško' : null].filter(Boolean).join(' · ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>{v.vin}</Table.Td>
                    <Table.Td ta="right" className="brez-preloma">
                      <Anchor component="button" size="sm" onClick={() => setUreja({ v, kaj: 'ddv' })}>
                        {ddv(v.ddvStopnja)}
                      </Anchor>
                    </Table.Td>
                    <Table.Td ta="right" className="brez-preloma">
                      <Anchor component="button" size="sm" fw={600} onClick={() => setUreja({ v, kaj: 'cena' })}>
                        {denar(v.cenaVozila)}
                      </Anchor>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Tooltip label="Odstrani z dokumenta">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => odstrani(i)}
                          aria-label="Odstrani vozilo"
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Kartica>
      <UrejanjeVozila ureja={ureja} zapri={() => setUreja(null)} />
    </Stack>
  );
}

/** Sprememba prodajne cene ali stopnje DDV vozila (shrani se na vozilo, kot v Swingu). */
function UrejanjeVozila({
  ureja,
  zapri,
}: {
  ureja: { v: PostavkaVozila; kaj: 'cena' | 'ddv' } | null;
  zapri: () => void;
}) {
  const odjemalec = useQueryClient();
  const [vrednost, setVrednost] = useState<number | string>('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [zadnji, setZadnji] = useState<typeof ureja>(null);
  if (ureja !== zadnji) {
    setZadnji(ureja);
    setNapaka(null);
    setVrednost((ureja?.kaj === 'cena' ? ureja.v.cenaVozila : ureja?.v.ddvStopnja) ?? '');
  }
  const cena = ureja?.kaj === 'cena';

  const shrani = async () => {
    if (!ureja) {
      return;
    }
    const n = stevilka(vrednost);
    if (cena ? n === undefined : n !== undefined && (n < 0 || n > 100)) {
      setNapaka(cena ? 'Vnesite ceno.' : 'Stopnja DDV je med 0 in 100 % (prazno = brez stopnje).');
      return;
    }
    setDela(true);
    try {
      const path = { id: ureja.v.voziloId! };
      await izvedi(
        cena
          ? api.PUT('/api/vozila/{id}/cena', { params: { path }, body: { vrednost: n } })
          : api.PUT('/api/vozila/{id}/ddv-stopnja', { params: { path }, body: { vrednost: n } }),
      );
      await odjemalec.invalidateQueries({ queryKey: ['izracun'] });
      await odjemalec.invalidateQueries({ queryKey: ['vozilo', ureja.v.voziloId] });
      zapri();
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };

  return (
    <Modal
      opened={ureja !== null}
      onClose={() => !dela && zapri()}
      title={cena ? 'Sprememba prodajne cene' : 'Sprememba stopnje DDV'}
      radius="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {cena
            ? 'Nova prodajna cena se shrani na vozilo in bo tako nastavljena tudi v prihodnje.'
            : 'Stopnja DDV se shrani na vozilo. Prazno pomeni brez stopnje.'}
        </Text>
        <NumberInput
          label={cena ? 'Prodajna cena (z DDV)' : 'Stopnja DDV (%)'}
          value={vrednost}
          onChange={setVrednost}
          decimalSeparator=","
          thousandSeparator={cena ? '.' : undefined}
          decimalScale={2}
          rightSection={cena ? '€' : '%'}
          error={napaka}
          data-autofocus
          onKeyDown={(e) => e.key === 'Enter' && void shrani()}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela}>
            Prekliči
          </Button>
          <Button onClick={() => void shrani()} loading={dela}>
            Shrani
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Korak Storitve (kot StoritvePanel): poišči storitev v šifrantu ali vpiši novo (strežnik jo ob izdaji doda v šifrant);
 * cena postavke = količina × cena enote (izračuna strežnik).
 */
export function StoritveIzdaje({
  storitve,
  dodaj,
  odstrani,
  napaka,
}: {
  storitve: PostavkaStoritve[];
  dodaj: (s: PostavkaStoritve) => void;
  odstrani: (index: number) => void;
  napaka?: string;
}) {
  const [naziv, setNaziv] = useState('');
  const [izbrana, setIzbrana] = useState<Shema['StoritevPodatki'] | null>(null);
  const [kolicina, setKolicina] = useState<number | string>(1);
  const [cenaEnote, setCenaEnote] = useState<number | string>(0);
  const [ddvStopnja, setDdvStopnja] = useState<number | string>('');
  const [napakaVnosa, setNapakaVnosa] = useState<Record<string, string>>({});
  const [zakasnjeno] = useDebouncedValue(naziv, 300);
  const zadetki = useQuery({
    queryKey: ['storitve-iskanje', zakasnjeno],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/storitve', { signal, params: { query: { iskanje: zakasnjeno || undefined } } })),
    placeholderData: keepPreviousData,
  });
  const poNazivu = new Map<string, Shema['StoritevPodatki']>();
  (zadetki.data ?? []).slice(0, 30).forEach((s) => s.naziv && !poNazivu.has(s.naziv) && poNazivu.set(s.naziv, s));

  const izberi = (n: string) => {
    const s = poNazivu.get(n);
    if (s) {
      setIzbrana(s);
      setCenaEnote(s.cenaEnote ?? 0);
      setDdvStopnja(s.ddvStopnja ?? '');
    }
  };

  const potrdi = () => {
    const n: Record<string, string> = {};
    const kol = stevilka(kolicina);
    const cena = stevilka(cenaEnote);
    const stopnja = stevilka(ddvStopnja);
    if (!naziv.trim()) {
      n.naziv = 'Poiščite storitev ali vpišite naziv nove.';
    }
    if (kol === undefined) {
      n.kolicina = 'Vnesite količino.';
    }
    if (cena === undefined) {
      n.cenaEnote = 'Vnesite ceno enote.';
    }
    setNapakaVnosa(n);
    if (Object.keys(n).length > 0) {
      return;
    }
    const obstojeca = izbrana && izbrana.naziv === naziv ? izbrana : null;
    dodaj({
      id: 0,
      storitevId: obstojeca?.id,
      naziv: naziv.trim(),
      kolicina: kol,
      cenaEnote: cena,
      cena: (kol ?? 0) * (cena ?? 0),
      ddvStopnja: stopnja,
    });
    setNaziv('');
    setIzbrana(null);
    setKolicina(1);
    setCenaEnote(0);
    setDdvStopnja('');
  };

  const stevilo = { decimalSeparator: ',', decimalScale: 2 } as const;
  return (
    <Stack gap="md">
      <Kartica naslov="Storitev">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Poiščite obstoječo storitev ali vpišite novo – nova se ob izdaji shrani v šifrant.
          </Text>
          <Autocomplete
            label="Naziv"
            placeholder="Naziv storitve"
            leftSection={<IconSearch size={16} />}
            value={naziv}
            onChange={(v) => {
              setNaziv(v);
              if (izbrana && izbrana.naziv !== v) {
                setIzbrana(null);
              }
            }}
            onOptionSubmit={izberi}
            data={[...poNazivu.keys()]}
            filter={({ options }) => options}
            error={napakaVnosa.naziv}
            size="md"
          />
          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
            <NumberInput
              label="Količina"
              value={kolicina}
              onChange={setKolicina}
              {...stevilo}
              error={napakaVnosa.kolicina}
              size="md"
            />
            <NumberInput
              label="Cena enote"
              value={cenaEnote}
              onChange={setCenaEnote}
              {...stevilo}
              thousandSeparator="."
              rightSection="€"
              error={napakaVnosa.cenaEnote}
              size="md"
            />
            <NumberInput
              label="Stopnja DDV (%)"
              placeholder="brez"
              value={ddvStopnja}
              onChange={setDdvStopnja}
              {...stevilo}
              min={0}
              max={100}
              size="md"
            />
          </SimpleGrid>
          <Group>
            <Button leftSection={<IconPlus size={18} />} onClick={potrdi}>
              Dodaj storitev
            </Button>
          </Group>
          {napaka && (
            <Text size="sm" c="red" role="alert">
              {napaka}
            </Text>
          )}
        </Stack>
      </Kartica>
      <Kartica naslov="Dodane storitve">
        <TabelaStoritev storitve={storitve} odstrani={odstrani} />
      </Kartica>
    </Stack>
  );
}

/** Storitve na dokumentu (z odstranjevanjem, če je podano). */
export function TabelaStoritev({
  storitve,
  odstrani,
}: {
  storitve: PostavkaStoritve[];
  odstrani?: (index: number) => void;
}) {
  if (storitve.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Ni dodanih storitev.
      </Text>
    );
  }
  return (
    <Table.ScrollContainer minWidth={520}>
      <Table className="stevilke" verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Storitev</Table.Th>
            <Table.Th ta="right">Kol.</Table.Th>
            <Table.Th ta="right">Enota</Table.Th>
            <Table.Th ta="right">DDV</Table.Th>
            <Table.Th ta="right">Za plačilo</Table.Th>
            {odstrani && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {storitve.map((s, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                {s.naziv}
                {!s.storitevId && (
                  <Text span size="xs" c="dimmed">
                    {' '}
                    (nova)
                  </Text>
                )}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {String(s.kolicina ?? '').replace('.', ',')}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(s.cenaEnote)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {ddv(s.ddvStopnja)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(s.cena)}
              </Table.Td>
              {odstrani && (
                <Table.Td ta="right">
                  <ActionIcon variant="subtle" color="red" onClick={() => odstrani(i)} aria-label="Odstrani storitev">
                    <IconTrash size={18} />
                  </ActionIcon>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Vozila v predogledu (cena postavke, kot jo izračuna strežnik). */
export function TabelaVozil({ vozila }: { vozila: PostavkaVozila[] }) {
  if (vozila.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Ni dodanih vozil.
      </Text>
    );
  }
  return (
    <Table.ScrollContainer minWidth={520}>
      <Table className="stevilke" verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Vozilo</Table.Th>
            <Table.Th>VIN</Table.Th>
            <Table.Th ta="right">DDV</Table.Th>
            <Table.Th ta="right">Za plačilo</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {vozila.map((v, i) => (
            <Table.Tr key={`${v.voziloId}-${i}`}>
              <Table.Td>{[v.znamka, v.tip].filter(Boolean).join(' ')}</Table.Td>
              <Table.Td>{v.vin}</Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {ddv(v.ddvStopnja)}
              </Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(v.cena)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Klavzule na dokumentu: dodajanje iz šifranta (kot IzberiKlavzulo) in odstranjevanje. */
export function KlavzuleIzdaje({ klavzule, nastavi }: { klavzule: Klavzula[]; nastavi: (k: Klavzula[]) => void }) {
  const sifrant = useQuery({
    queryKey: ['klavzule'],
    queryFn: ({ signal }) => podatki(api.GET('/api/klavzule', { signal })),
  });
  const moznosti = (sifrant.data ?? []).filter((k) => k.besedilo && !klavzule.some((x) => x.besedilo === k.besedilo));
  return (
    <Kartica naslov="Klavzule">
      <Stack gap="xs">
        {klavzule.length === 0 && (
          <Text size="sm" c="dimmed">
            Brez klavzul.
          </Text>
        )}
        {klavzule.map((k, i) => (
          <Paper key={i} withBorder radius="md" p="xs">
            <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
              <Text size="sm" style={{ whiteSpace: 'pre-wrap', minWidth: 0 }}>
                {k.besedilo}
              </Text>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => nastavi(klavzule.filter((_, j) => j !== i))}
                aria-label="Odstrani klavzulo"
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Group>
          </Paper>
        ))}
        <Select
          aria-label="Dodaj klavzulo"
          placeholder="Dodaj klavzulo …"
          leftSection={<IconPlus size={16} />}
          searchable
          data={moznosti.map((k) => ({ value: String(k.id), label: k.besedilo! }))}
          value={null}
          onChange={(v) => {
            const k = moznosti.find((x) => String(x.id) === v);
            if (k) {
              nastavi([...klavzule, { id: 0, besedilo: k.besedilo, dodajZaVozilo: !!k.dodajZaVozilo }]);
            }
          }}
          nothingFoundMessage="Ni klavzul v šifrantu"
        />
      </Stack>
    </Kartica>
  );
}

/**
 * Spodnja vrstica (kot v Swingovem koraku Predogled): skupni znesek, razlog, zakaj izdaja ni mogoča (tik ob gumbih),
 * predogled PDF in izdaja. Ostane vidna med vsemi koraki.
 */
export function VrsticaIzdaje({
  znesek,
  razlog,
  predogled,
  izdaj,
  naziv,
  dela,
}: {
  znesek?: number;
  razlog: string | null;
  predogled: () => void;
  izdaj: () => void;
  naziv: string;
  dela: 'predogled' | 'izdaja' | null;
}) {
  return (
    <Paper
      shadow="md"
      radius="lg"
      p="sm"
      mt="md"
      style={{ position: 'sticky', bottom: 8, zIndex: 5, border: `1px solid ${barve.obroba}` }}
    >
      <Group justify="space-between" gap="sm" wrap="wrap">
        <Group gap={6} align="baseline">
          <Text size="sm" c="dimmed">
            Skupni znesek za plačilo
          </Text>
          <Text fw={700} size="lg" className="brez-preloma">
            {denar(znesek ?? 0)} €
          </Text>
        </Group>
        {razlog && (
          <Group gap={6} wrap="nowrap" style={{ flex: '1 1 240px', minWidth: 0 }} role="alert">
            <IconAlertTriangle size={18} color={barve.napaka} style={{ flexShrink: 0 }} />
            <Text size="sm" fw={600} c="red">
              {razlog}
            </Text>
          </Group>
        )}
        <Group gap="xs" style={{ marginLeft: 'auto' }}>
          <Button
            variant="default"
            leftSection={<IconEye size={18} />}
            onClick={predogled}
            loading={dela === 'predogled'}
            disabled={dela === 'izdaja'}
          >
            Predogled
          </Button>
          <Button onClick={izdaj} loading={dela === 'izdaja'} disabled={dela === 'predogled'}>
            {naziv}
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}
